import { useState, useEffect, useCallback, useRef } from 'react'
import { getFacts, deleteFact, setFactVerdict, getFactContradictions, dismissContradiction, startContradictionDetection } from '../utils/api'
import { apiFetch } from '../utils/api'
import ModelToggle from './ModelToggle'
import { getModelPref } from '../utils/modelPrefs'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

const SOURCE_LABEL = {
  resume: 'резюме', journal: 'дневник', personal: 'личная переписка',
  group: 'группа', threads: 'threads', survey: 'анкета', unknown: '?',
}
const SOURCE_COLOR = {
  resume: 'rgba(120,160,255,0.13)', journal: 'rgba(120,220,160,0.13)',
  personal: 'rgba(200,169,110,0.13)', group: 'rgba(180,185,212,0.09)',
  threads: 'rgba(160,120,220,0.13)', survey: 'rgba(255,200,80,0.13)',
  unknown: 'rgba(180,185,212,0.07)',
}
const CATEGORY_LABEL = {
  person: 'Люди', event: 'События', preference: 'Предпочтения',
  worldview: 'Взгляды', professional: 'Профессиональное',
}
const C = {
  bg: '#05050d', surface: 'rgba(255,255,255,0.028)', border: 'rgba(180,185,230,0.09)',
  accent: '#b8bdd4', text: '#e8e8f0', muted: 'rgba(180,185,212,0.5)',
  danger: 'rgba(220,80,80,0.75)', green: 'rgba(80,200,120,0.85)', red: 'rgba(220,80,80,0.85)',
  greenBg: 'rgba(80,200,120,0.10)', redBg: 'rgba(220,80,80,0.10)',
  yellow: 'rgba(240,190,60,0.85)', yellowBg: 'rgba(240,190,60,0.10)',
  glassBg: 'rgba(184,189,212,0.04)',
  glassBorder: 'rgba(184,189,212,0.12)',
}

// ── Spinner SVG ─────────────────────────────────────────────────────────────
function Spinner({ size = 14, color = C.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.9s linear infinite', flexShrink: 0 }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" strokeOpacity="0.25"/>
      <path d="M12 3a9 9 0 0 1 9 9" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  )
}

// ── Batch status card ────────────────────────────────────────────────────────
function BatchCard({ index, state, isActive, onClick }) {
  const s = state || { status: 'pending', retries: 0, facts: 0 }
  const isDone = s.status === 'done'
  const isRunning = s.status === 'running'
  const isRetrying = s.status === 'retrying'
  const isFailed = s.status === 'error'
  const isPending = s.status === 'pending'

  const borderColor = isDone
    ? 'rgba(80,200,120,0.30)'
    : isFailed
    ? 'rgba(220,80,80,0.35)'
    : isRetrying
    ? 'rgba(240,190,60,0.30)'
    : isRunning
    ? 'rgba(184,189,212,0.20)'
    : 'rgba(184,189,212,0.07)'

  const bgColor = isDone
    ? 'rgba(80,200,120,0.06)'
    : isFailed
    ? 'rgba(220,80,80,0.08)'
    : isRetrying
    ? 'rgba(240,190,60,0.06)'
    : isRunning
    ? 'rgba(184,189,212,0.05)'
    : 'rgba(255,255,255,0.015)'

  return (
    <div
      onClick={onClick}
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        transition: 'all 0.3s',
        backdropFilter: 'blur(8px)',
        position: 'relative',
        minHeight: 72,
        cursor: 'pointer',
        outline: isActive ? `2px solid ${C.accent}` : 'none',
        outlineOffset: 1,
      }}>
      {/* Retry badge */}
      {(isRetrying || isFailed || (isDone && s.retries > 0)) && (
        <div style={{
          position: 'absolute', top: 6, right: 8,
          fontFamily: 'Space Mono, monospace', fontSize: 9,
          color: isFailed ? C.red : isRetrying ? C.yellow : 'rgba(240,190,60,0.6)',
          background: isFailed ? C.redBg : isRetrying ? C.yellowBg : 'rgba(240,190,60,0.06)',
          border: `1px solid ${isFailed ? 'rgba(220,80,80,0.30)' : 'rgba(240,190,60,0.20)'}`,
          borderRadius: 4, padding: '1px 5px',
        }}>
          ↺ {s.retries}
        </div>
      )}

      {/* Batch label */}
      <div style={{
        fontFamily: 'Space Mono, monospace', fontSize: 10,
        color: isDone ? 'rgba(80,200,120,0.7)' : isFailed ? C.red : isRetrying ? C.yellow : C.muted,
      }}>
        {String(index + 1).padStart(2, '0')}
      </div>

      {/* Status indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto' }}>
        {isRunning && <Spinner size={12} color={C.accent} />}
        {isRetrying && <Spinner size={12} color={C.yellow} />}
        {isDone && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="rgba(80,200,120,0.20)"/>
            <path d="M7 12l3.5 3.5L17 9" stroke={C.green} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        {isFailed && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="rgba(220,80,80,0.20)"/>
            <path d="M8 8l8 8M16 8l-8 8" stroke={C.red} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        {isPending && (
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(184,189,212,0.18)' }} />
        )}

        <span style={{
          fontFamily: 'Inter, sans-serif', fontSize: 11,
          color: isDone ? C.green : isFailed ? C.red : isRetrying ? C.yellow : isRunning ? C.accent : 'rgba(184,189,212,0.35)',
        }}>
          {isDone
            ? `${s.facts} фактов`
            : isFailed
            ? (s.fail_reason === 'rate_limited' ? 'не удалось (квота)' : s.fail_reason === 'max_tokens' ? 'не удалось (токены)' : 'не удалось')
            : isRetrying
            ? `повтор ${s.retries}...`
            : isRunning
            ? 'обработка'
            : 'ожидание'}
        </span>
      </div>
    </div>
  )
}

// ── Extraction progress panel ────────────────────────────────────────────────
function ExtractionProgress({ jobId, userId, onDone, isOwner, activeBatch, onBatchClick }) {
  const [jobData, setJobData] = useState(null)
  const pollRef = useRef(true)

  useEffect(() => {
    if (!jobId) return
    pollRef.current = true

    const poll = async () => {
      while (pollRef.current) {
        try {
          const res = await fetch(`${BASE_URL}/api/analyze/job/${jobId}`)
          if (res.ok) {
            const data = await res.json()
            setJobData(data)
            if (data.status === 'done' || data.status === 'error') {
              pollRef.current = false
              if (data.status === 'done') onDone?.(data.result)
              break
            }
          }
        } catch (_) {}
        await new Promise(r => setTimeout(r, 1500))
      }
    }
    poll()
    return () => { pollRef.current = false }
  }, [jobId, onDone])

  if (!jobId || !jobData) return null

  const total = jobData.total_batches || 0
  const done = jobData.done_batches || 0
  const factsSoFar = jobData.facts_so_far || 0
  const batchStates = jobData.batch_states || []
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const isError = jobData.status === 'error'
  const isDone = jobData.status === 'done'

  // Count running/retrying/failed
  const running = batchStates.filter(b => b.status === 'running').length
  const retrying = batchStates.filter(b => b.status === 'retrying').length
  const failedCount = jobData.result?.failed_batches?.length ?? batchStates.filter(b => b.status === 'error').length

  // Real failure reasons, not a hardcoded guess — backend now tags each lost batch as
  // rate_limited (429) or max_tokens, so the message reflects what actually happened.
  const REASON_TEXT = {
    rate_limited: 'Gemini был перегружен — превышена квота запросов (429)',
    max_tokens: 'Gemini обрезал ответ по лимиту токенов на каждой попытке',
    other: 'сетевая или временная ошибка',
  }
  const failReasons = new Set()
  const failedList = jobData.result?.failed_batches
  if (Array.isArray(failedList)) {
    failedList.forEach(b => (b?.reasons || []).forEach(r => failReasons.add(r)))
  } else {
    batchStates.forEach(b => { if (b.status === 'error' && b.fail_reason) failReasons.add(b.fail_reason) })
  }
  const failReasonPhrase = failReasons.size > 0
    ? [...failReasons].map(r => REASON_TEXT[r] || REASON_TEXT.other).join('; ')
    : REASON_TEXT.other

  return (
    <div style={{
      background: 'rgba(184,189,212,0.03)',
      border: `1px solid ${isError ? 'rgba(220,80,80,0.25)' : isDone ? 'rgba(80,200,120,0.25)' : C.glassBorder}`,
      borderRadius: 14,
      padding: '20px 20px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      backdropFilter: 'blur(12px)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!isDone && !isError && <Spinner size={14} color={C.accent} />}
        {isDone && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="rgba(80,200,120,0.20)"/>
            <path d="M7 12l3.5 3.5L17 9" stroke={C.green} strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        )}
        {isError && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="rgba(220,80,80,0.20)"/>
            <path d="M12 7v6M12 17h.01" stroke={C.red} strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        )}

        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 17, fontWeight: 600, color: C.text }}>
          {isDone ? 'Извлечение завершено' : isError ? 'Ошибка извлечения' : 'Извлечение фактов'}
        </span>

        <div style={{ flex: 1 }} />

        {/* Stat pills */}
        <div style={{ display: 'flex', gap: 8 }}>
          {factsSoFar > 0 && (
            <span style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10,
              color: C.green, background: C.greenBg,
              border: `1px solid rgba(80,200,120,0.20)`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              {factsSoFar} фактов
            </span>
          )}
          {running > 0 && (
            <span style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10,
              color: C.accent, background: 'rgba(184,189,212,0.08)',
              border: `1px solid rgba(184,189,212,0.15)`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              {running} активно
            </span>
          )}
          {retrying > 0 && (
            <span style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10,
              color: C.yellow, background: C.yellowBg,
              border: `1px solid rgba(240,190,60,0.20)`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              {retrying} повтор
            </span>
          )}
          {isOwner && isDone && jobData.result?.gemini_cost != null && (
            <span style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10,
              color: C.accent, background: 'rgba(184,189,212,0.08)',
              border: `1px solid rgba(184,189,212,0.15)`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              {`gemini $${jobData.result.gemini_cost.toFixed(4)}`}
              {jobData.result.total_cost != null && ` · всего $${jobData.result.total_cost.toFixed(4)}`}
            </span>
          )}
          {isOwner && isDone && failedCount > 0 && (
            <span style={{
              fontFamily: 'Space Mono, monospace', fontSize: 10,
              color: C.red, background: C.redBg,
              border: `1px solid rgba(220,80,80,0.20)`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              ⚠ {failedCount} провалено
            </span>
          )}
        </div>
      </div>

      {isOwner && isDone && failedCount > 0 && (
        <div style={{
          fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.red, lineHeight: 1.5,
          background: C.redBg, border: `1px solid rgba(220,80,80,0.20)`,
          borderRadius: 8, padding: '10px 12px',
        }}>
          ⚠ {failedCount} из {total} батчей не удалось обработать ({failReasonPhrase}) — данные по ним отсутствуют в этом прогоне. Извлечение в целом завершилось, но неполное. Чтобы восполнить — запустите переизвлечение ещё раз.
        </div>
      )}

      {/* Progress bar */}
      {total > 0 && (
        <div>
          <div style={{
            height: 4, borderRadius: 4,
            background: 'rgba(184,189,212,0.08)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              borderRadius: 4,
              background: isDone
                ? 'rgba(80,200,120,0.70)'
                : isError
                ? 'rgba(220,80,80,0.70)'
                : 'linear-gradient(90deg, rgba(184,189,212,0.5) 0%, rgba(184,189,212,0.8) 100%)',
              transition: 'width 0.6s ease',
              boxShadow: isDone ? '0 0 8px rgba(80,200,120,0.35)' : '0 0 8px rgba(184,189,212,0.20)',
            }} />
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: 5,
            fontFamily: 'Space Mono, monospace', fontSize: 10, color: C.muted,
          }}>
            <span>{done} из {total} батчей</span>
            <span>{pct}%</span>
          </div>
        </div>
      )}

      {/* Batch grid */}
      {total > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(total, 7)}, 1fr)`,
          gap: 8,
        }}>
          {Array.from({ length: total }).map((_, i) => (
            <BatchCard
              key={i}
              index={i}
              state={batchStates[i]}
              isActive={activeBatch === String(i + 1)}
              onClick={() => onBatchClick?.(String(i + 1))}
            />
          ))}
        </div>
      )}

      {isError && jobData.error && (
        <div style={{
          fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.red,
          background: C.redBg, border: `1px solid rgba(220,80,80,0.20)`,
          borderRadius: 8, padding: '8px 12px',
        }}>
          {jobData.error}
        </div>
      )}
    </div>
  )
}

// ── Contradiction detection progress (manual trigger — temporary, see task backlog) ──
function ContradictionProgress({ jobId, onDone }) {
  const [jobData, setJobData] = useState(null)
  const pollRef = useRef(true)

  useEffect(() => {
    if (!jobId) return
    pollRef.current = true
    const poll = async () => {
      while (pollRef.current) {
        try {
          const res = await fetch(`${BASE_URL}/api/analyze/job/${jobId}`)
          if (res.ok) {
            const data = await res.json()
            setJobData(data)
            if (data.status === 'done' || data.status === 'error') {
              pollRef.current = false
              if (data.status === 'done') onDone?.(data.result)
              break
            }
          }
        } catch (_) {}
        await new Promise(r => setTimeout(r, 1500))
      }
    }
    poll()
    return () => { pollRef.current = false }
  }, [jobId, onDone])

  if (!jobId || !jobData) return null
  const isDone = jobData.status === 'done'
  const isError = jobData.status === 'error'
  const total = jobData.total_entities || 0
  const done = jobData.done_entities || 0

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: 'Inter, sans-serif', fontSize: 12, color: isError ? C.red : C.muted,
    }}>
      {!isDone && !isError && <Spinner size={12} />}
      {isDone
        ? `Готово: найдено ${jobData.result?.contradictions_found ?? 0} противоречий (проверено ${jobData.result?.entities_checked ?? total} сущностей)`
        : isError
        ? `Ошибка: ${jobData.error}`
        : `Проверяю сущности… ${done}/${total || '?'}`}
    </div>
  )
}

// ── Main FactsViewer ─────────────────────────────────────────────────────────
export default function FactsViewer({ userId, onCountChange, extractionJobId, isOwner = false }) {
  const [facts, setFacts] = useState({})
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [collapsed, setCollapsed] = useState({})
  const [verdicts, setVerdicts] = useState({})
  const [pendingVerdict, setPendingVerdict] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState(null)
  const [batchFilter, setBatchFilter] = useState(null)
  const [contradictions, setContradictions] = useState([])
  const [showContradictionsOnly, setShowContradictionsOnly] = useState(false)
  const [contradictionJobId, setContradictionJobId] = useState(null)
  const [pendingContradiction, setPendingContradiction] = useState(null)
  const liveRef = useRef(null)

  const loadContradictions = useCallback(async () => {
    try {
      const res = await getFactContradictions(userId)
      setContradictions(res.contradictions || [])
    } catch { /* leave previous state */ }
  }, [userId])

  useEffect(() => { loadContradictions() }, [loadContradictions])

  const handleRunContradictionCheck = async () => {
    try {
      const jobId = await startContradictionDetection(userId, getModelPref('contradictions'))
      setContradictionJobId(jobId)
    } catch (e) {
      alert(e.message || 'Не удалось запустить проверку')
    }
  }

  const handleContradictionDone = useCallback(() => {
    loadContradictions()
  }, [loadContradictions])

  const handleDismissContradiction = async (contradictionId) => {
    setPendingContradiction(contradictionId)
    try {
      await dismissContradiction(contradictionId)
      setContradictions(prev => prev.filter(c => c.id !== contradictionId))
    } finally {
      setPendingContradiction(null)
    }
  }

  const handleBatchClick = useCallback((label) => {
    setBatchFilter(prev => prev === label ? null : label)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getFacts(userId)
      if (res.facts) {
        setFacts(res.facts)
        const t = res.total || 0
        setTotal(t)
        onCountChange?.(t)
        const init = {}
        for (const list of Object.values(res.facts)) {
          for (const f of list) {
            if (f.user_verdict) init[f.id] = f.user_verdict
          }
        }
        setVerdicts(init)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, onCountChange])

  useEffect(() => { load() }, [load])

  // Live reload during extraction — poll facts every 4s while job is running
  useEffect(() => {
    if (!extractionJobId) { clearInterval(liveRef.current); return }
    liveRef.current = setInterval(() => { load() }, 4000)
    return () => clearInterval(liveRef.current)
  }, [extractionJobId, load])

  const handleExtractionDone = useCallback((result) => {
    clearInterval(liveRef.current)
    load()
    if (result?.contradiction_job_id) setContradictionJobId(result.contradiction_job_id)
  }, [load])

  const handleVerdict = async (factId, verdict) => {
    const current = verdicts[factId] || null
    const newVerdict = current === verdict ? null : verdict
    setPendingVerdict(factId)
    setVerdicts(p => ({ ...p, [factId]: newVerdict }))
    try {
      await setFactVerdict(userId, factId, newVerdict)
    } catch {
      setVerdicts(p => ({ ...p, [factId]: current }))
    } finally {
      setPendingVerdict(null)
    }
  }

  const handleDelete = async (factId) => {
    setPendingVerdict(factId)
    try {
      await deleteFact(userId, factId)
      setFacts(prev => {
        const next = {}
        let newTotal = total
        for (const [entity, list] of Object.entries(prev)) {
          const filtered = list.filter(f => f.id !== factId)
          if (filtered.length !== list.length) newTotal--
          if (filtered.length > 0) next[entity] = filtered
        }
        setTotal(newTotal)
        onCountChange?.(newTotal)
        return next
      })
      setVerdicts(p => { const n = { ...p }; delete n[factId]; return n })
    } finally {
      setPendingVerdict(null)
    }
  }

  // factId -> [{ contradictionId, otherText, explanation }] — a fact can appear in more than one pair
  const factContradictionMap = {}
  for (const c of contradictions) {
    if (!factContradictionMap[c.fact_a_id]) factContradictionMap[c.fact_a_id] = []
    factContradictionMap[c.fact_a_id].push({ contradictionId: c.id, otherText: c.fact_b_text, explanation: c.explanation })
    if (!factContradictionMap[c.fact_b_id]) factContradictionMap[c.fact_b_id] = []
    factContradictionMap[c.fact_b_id].push({ contradictionId: c.id, otherText: c.fact_a_text, explanation: c.explanation })
  }

  const filteredFacts = {}
  for (const [entity, list] of Object.entries(facts)) {
    let filtered = categoryFilter ? list.filter(f => f.category === categoryFilter) : list
    if (batchFilter) filtered = filtered.filter(f => f.batch_label === batchFilter)
    if (showContradictionsOnly) filtered = filtered.filter(f => factContradictionMap[f.id]?.length > 0)
    if (filtered.length > 0) filteredFacts[entity] = filtered
  }

  const entities = Object.keys(filteredFacts).sort((a, b) => {
    if (a === 'автор') return -1
    if (b === 'автор') return 1
    return a.localeCompare(b, 'ru')
  })

  const categories = ['all', 'preference', 'worldview', 'professional', 'event', 'person']
  const rejectedCount = Object.values(verdicts).filter(v => v === 'rejected').length
  const confirmedCount = Object.values(verdicts).filter(v => v === 'confirmed').length
  const hasExtraction = !!extractionJobId
  const contradictionCount = contradictions.length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Extraction progress panel */}
      {hasExtraction && (
        <ExtractionProgress
          jobId={extractionJobId}
          userId={userId}
          onDone={handleExtractionDone}
          isOwner={isOwner}
          activeBatch={batchFilter}
          onBatchClick={handleBatchClick}
        />
      )}

      {/* Manual contradiction check — temporary, for backfilling facts extracted before
          auto-detection existed. Normal flow runs this automatically after extraction. */}
      {total > 0 && !contradictionJobId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={handleRunContradictionCheck}
            style={{
              padding: '5px 12px', borderRadius: 6,
              border: `1px solid ${C.border}`, background: 'none',
              color: C.muted, fontFamily: 'Space Mono, monospace', fontSize: 10.5, cursor: 'pointer',
            }}
          >
            ⚠ Найти противоречия
          </button>
          <ModelToggle prefKey="contradictions" />
        </div>
      )}
      {contradictionJobId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ContradictionProgress jobId={contradictionJobId} onDone={() => { loadContradictions() }} />
          <button
            onClick={() => setContradictionJobId(null)}
            style={{ background: 'none', border: 'none', color: C.muted, fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}
          >
            ↻ ещё раз
          </button>
        </div>
      )}

      {/* Facts list */}
      {loading && Object.keys(facts).length === 0 ? (
        <div style={{ padding: '16px 0', color: C.muted, fontFamily: 'Inter, sans-serif', fontSize: 13 }}>
          загрузка фактов…
        </div>
      ) : Object.keys(facts).length === 0 && !hasExtraction ? (
        <div style={{ padding: '16px 0', color: C.muted, fontFamily: 'Inter, sans-serif', fontSize: 13 }}>
          Факты ещё не извлечены. Они появятся автоматически после загрузки данных.
        </div>
      ) : Object.keys(facts).length > 0 ? (
        <>
          {/* Stats + filter bar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.muted }}>
              {total} фактов
              {hasExtraction && <span style={{ color: C.accent, marginLeft: 6 }}> · обновляется…</span>}
              {rejectedCount > 0 && <span style={{ color: C.red, marginLeft: 8 }}>· {rejectedCount} ложь</span>}
              {confirmedCount > 0 && <span style={{ color: C.green, marginLeft: 8 }}>· {confirmedCount} подтверждено</span>}
            </span>
            {batchFilter && (
              <button
                onClick={() => setBatchFilter(null)}
                style={{
                  padding: '3px 10px', borderRadius: 4,
                  border: `1px solid ${C.accent}`,
                  background: `${C.accent}18`,
                  color: C.accent,
                  fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer',
                }}
              >
                батч {batchFilter} ✕
              </button>
            )}
            {contradictionCount > 0 && (
              <button
                onClick={() => setShowContradictionsOnly(v => !v)}
                style={{
                  padding: '3px 10px', borderRadius: 4,
                  border: `1px solid ${showContradictionsOnly ? C.yellow : 'rgba(240,190,60,0.35)'}`,
                  background: showContradictionsOnly ? C.yellowBg : 'none',
                  color: C.yellow,
                  fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer',
                }}
              >
                ⚠ Противоречия ({contradictionCount})
              </button>
            )}
            <div style={{ flex: 1 }} />
            {categories.map(cat => {
              const isActive = cat === 'all' ? !categoryFilter : categoryFilter === cat
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat === 'all' ? null : (categoryFilter === cat ? null : cat))}
                  style={{
                    padding: '3px 10px', borderRadius: 4,
                    border: `1px solid ${isActive ? C.accent : C.border}`,
                    background: isActive ? `${C.accent}18` : 'none',
                    color: isActive ? C.accent : C.muted,
                    fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer',
                  }}
                >
                  {cat === 'all' ? 'Все' : CATEGORY_LABEL[cat] || cat}
                </button>
              )
            })}
          </div>

          {/* Entity groups */}
          {entities.map(entity => {
            const list = filteredFacts[entity] || []
            const isCollapsed = collapsed[entity]
            const isMe = entity === 'автор'
            const rejectedInGroup = list.filter(f => (verdicts[f.id] ?? null) === 'rejected').length
            return (
              <div key={entity} style={{
                background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 10, overflow: 'hidden',
              }}>
                <button
                  onClick={() => setCollapsed(p => ({ ...p, [entity]: !p[entity] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: 'none', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 16, fontWeight: 600, color: isMe ? C.accent : C.text, flex: 1 }}>
                    {isMe ? '◈ я (автор)' : entity}
                  </span>
                  {rejectedInGroup > 0 && (
                    <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 10, color: C.red }}>
                      {rejectedInGroup} ложь
                    </span>
                  )}
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.muted }}>
                    {list.length} фактов
                  </span>
                  <span style={{ color: C.muted, fontSize: 11 }}>{isCollapsed ? '▶' : '▼'}</span>
                </button>

                {!isCollapsed && (
                  <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {list.map(f => {
                      const verdict = verdicts[f.id] ?? null
                      const isRejected = verdict === 'rejected'
                      const isConfirmed = verdict === 'confirmed'
                      const isPending = pendingVerdict === f.id
                      const factContradictions = factContradictionMap[f.id] || []
                      return (
                        <div key={f.id} style={{
                          display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 10px',
                          background: isRejected ? C.redBg : isConfirmed ? C.greenBg : (SOURCE_COLOR[f.source_type] || SOURCE_COLOR.unknown),
                          borderRadius: 6,
                          border: `1px solid ${factContradictions.length > 0 ? 'rgba(240,190,60,0.35)' : isRejected ? 'rgba(220,80,80,0.25)' : isConfirmed ? 'rgba(80,200,120,0.20)' : 'transparent'}`,
                          opacity: isRejected ? 0.6 : 1,
                          transition: 'all 0.15s',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            <button
                              onClick={() => !isPending && handleVerdict(f.id, 'confirmed')}
                              disabled={isPending}
                              title="Подтвердить факт"
                              style={{
                                width: 22, height: 22, borderRadius: 4,
                                border: `1px solid ${isConfirmed ? C.green : 'rgba(80,200,120,0.25)'}`,
                                background: isConfirmed ? 'rgba(80,200,120,0.25)' : 'none',
                                color: isConfirmed ? C.green : 'rgba(80,200,120,0.5)',
                                cursor: isPending ? 'wait' : 'pointer', fontSize: 11,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.12s',
                              }}
                            >✓</button>
                            <button
                              onClick={() => !isPending && handleVerdict(f.id, 'rejected')}
                              disabled={isPending}
                              title="Пометить как ложь"
                              style={{
                                width: 22, height: 22, borderRadius: 4,
                                border: `1px solid ${isRejected ? C.red : 'rgba(220,80,80,0.25)'}`,
                                background: isRejected ? 'rgba(220,80,80,0.25)' : 'none',
                                color: isRejected ? C.red : 'rgba(220,80,80,0.5)',
                                cursor: isPending ? 'wait' : 'pointer', fontSize: 11,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.12s',
                              }}
                            >✗</button>
                          </div>

                          <span style={{
                            fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.text, flex: 1,
                            textDecoration: isRejected ? 'line-through' : 'none',
                          }}>
                            {f.fact}
                          </span>

                          {f.fact_date && (
                            <span style={{
                              fontFamily: 'Space Mono, monospace', fontSize: 10,
                              color: 'rgba(180,185,212,0.7)', whiteSpace: 'nowrap',
                              background: 'rgba(180,185,212,0.08)', padding: '1px 5px', borderRadius: 3,
                            }}>
                              {f.fact_date}
                            </span>
                          )}
                          {f.category && (
                            <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 10, color: 'rgba(180,185,212,0.5)', whiteSpace: 'nowrap' }}>
                              {CATEGORY_LABEL[f.category] || f.category}
                            </span>
                          )}
                          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>
                            {SOURCE_LABEL[f.source_type] || f.source_type}
                          </span>
                          <button
                            onClick={() => !isPending && handleDelete(f.id)}
                            disabled={isPending}
                            title="удалить факт"
                            style={{
                              background: 'none', border: 'none',
                              cursor: isPending ? 'wait' : 'pointer',
                              color: C.danger, fontSize: 14, padding: '0 2px',
                              lineHeight: 1, opacity: 0.5, transition: 'opacity 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
                          >✕</button>
                        </div>

                        {factContradictions.map(fc => (
                          <div key={fc.contradictionId} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '6px 8px', marginLeft: 30,
                            background: C.yellowBg, borderRadius: 5,
                            border: '1px solid rgba(240,190,60,0.2)',
                          }}>
                            <span style={{ fontSize: 12, color: C.yellow, flexShrink: 0 }}>⚠</span>
                            <div style={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 12, color: C.text, lineHeight: 1.4 }}>
                              <div style={{ color: C.muted }}>противоречит: «{fc.otherText}»</div>
                              {fc.explanation && <div style={{ marginTop: 2 }}>{fc.explanation}</div>}
                            </div>
                            <button
                              onClick={() => !pendingContradiction && handleDismissContradiction(fc.contradictionId)}
                              disabled={pendingContradiction === fc.contradictionId}
                              style={{
                                flexShrink: 0, padding: '2px 8px', borderRadius: 4,
                                border: `1px solid ${C.border}`, background: 'none',
                                color: C.muted, fontFamily: 'Space Mono, monospace', fontSize: 9.5,
                                cursor: pendingContradiction ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              это не противоречие
                            </button>
                          </div>
                        ))}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </>
      ) : null}
    </div>
  )
}
